import {Grid, Theme, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import React, {useEffect} from 'react';
import clsx from 'clsx';
import BillingParty from './BillingParty';
import {BlockTab, BlockTabs} from '../BlockTabs';
import AddIcon from '@material-ui/icons/Add';
import {BillingPartyData, BookingData, GuestData, newBillingPartyData} from '../helperFunctions';
import {CtaSettingsType} from '../BookingTool';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(0),
        paddingBottom: theme.spacing(3),

        [theme.breakpoints.down('sm')]: {
            margin: theme.spacing(3),
        },
    },
    padBottom: {
        paddingBottom: theme.spacing(3),
    },
    heading: {
        fontFamily: 'Recoleta-SemiBold',
    },
    body: {
        fontFamily: 'Nunito',
        fontSize: 18,
    },
    warningText: {
        color: theme.palette.error.main,
        paddingTop: theme.spacing(1),
    },
}));

interface Props {
    bookingData: BookingData;
    setBookingData: (data: BookingData) => void;
    allGuestData: GuestData[];
    setCtaSettings: (ctaSettings: CtaSettingsType | null) => void;
    billingPartyEmpty: boolean;
};

const Step4BillingDetails = (props: Props) => {
    const {bookingData, setBookingData, allGuestData, setCtaSettings, billingPartyEmpty} = props;
    const classes = useStyles();
    const errors: string[] = [];
    const bookingPartiesValid = bookingData.validData[4];

    const addBillingParty = () => {
        const billingPartiesData = [...bookingData.billingPartiesData, newBillingPartyData()];
        const isValid = billingPartiesData.find(data => !data.isValid) === undefined;
        const validData = bookingData.validData.map((data, i) => i === 4 ? isValid : data);
        setBookingData({...bookingData, billingPartiesData, validData});
    };

    const removeBillingParty = (targetData: BillingPartyData) => {
        const billingPartiesData: BillingPartyData[] = bookingData.billingPartiesData.filter(partyData => partyData !== targetData);
        //const billingPartiesData = filtered.map((partyData, i) => ({...partyData, num: i}));
        const isValid = billingPartiesData.find(data => !data.isValid) === undefined;
        const validData = bookingData.validData.map((data, i) => i === 4 ? isValid : data);

        //unlink guests.
        const roomsData = bookingData.roomsData.map(data => ({
            ...data,
            guestsData: data.guestsData.map(guest => ({
                ...guest,
                billingPartyId: guest.billingPartyId === targetData.id ? '' : guest.billingPartyId,
            }))
        }));

        setBookingData({...bookingData, billingPartiesData, validData, roomsData});
        // setSelectedTab(0);
    };

    const setBillingPartyData = (billingPartyData: BillingPartyData) => {
        const changeOnlyParty = bookingData.billingPartiesData.find(partyData => partyData.id === billingPartyData.id)!.onlyParty !== billingPartyData.onlyParty;
        const billingPartiesData: BillingPartyData[] = bookingData.billingPartiesData.map(partyData => partyData.id !== billingPartyData.id ? partyData : billingPartyData);
        let roomsData = [...bookingData.roomsData];

        if (billingPartyData.onlyParty && changeOnlyParty) {
            //If we have set the current party to be responsibe for all travellers
            roomsData = bookingData.roomsData.map(roomData => ({
                ...roomData, guestsData: roomData.guestsData.map(guestData => ({
                    ...guestData,
                    billingPartyId: billingPartyData.id,
                })),
            }));
        }
        const isValid = billingPartiesData.find(data => !data.isValid) === undefined;
        const validData = bookingData.validData.map((data, i) => i === 4 ? isValid : data);

        setBookingData({...bookingData, billingPartiesData, roomsData, validData});
    };

    const setGuestBillingParty = (guestData: GuestData, billingPartyId: string) => {
        const newGuestData = {...guestData, billingPartyId};
        const roomData = bookingData.roomsData.find(data => data.id === guestData.roomId);
        const guestsData = roomData!.guestsData.map(data => data.id === guestData.id ? newGuestData : data);
        const roomsData = bookingData.roomsData.map(data => data.id === roomData!.id ? {
            ...data,
            guestsData
        } : data);
        setBookingData({...bookingData, roomsData});
    }

    const toggleRemainingGuestsParty = (billingPartyId: string, toggleOn: boolean) => {
        const roomsData = bookingData.roomsData.map((roomData) => {
            const guestsData = roomData!.guestsData;
            return {
                ...roomData,
                guestsData: guestsData.map((guestData) => {
                    let newBillingPartyId = guestData.billingPartyId;

                    if (toggleOn && guestData.billingPartyId === '') {
                        newBillingPartyId = billingPartyId;
                    } else if (!toggleOn && guestData.billingPartyId === billingPartyId) {
                        newBillingPartyId = '';
                    }

                    return {
                        ...guestData,
                        billingPartyId: newBillingPartyId,
                    };
                })
            };
        });
        setBookingData({...bookingData, roomsData});
    };

    useEffect(() => {
        let isValid = !bookingData.billingPartiesData.find(data => !data.isValid);
        if (allGuestData.find(guestData => guestData.billingPartyId === '') !== undefined) isValid = false;

        if (bookingPartiesValid !== isValid) {
            const validData = bookingData.validData.map((data, i) => i === 4 ? isValid : data);
            setBookingData({...bookingData, validData});
        }
    }, [allGuestData]);

    useEffect(() => {
        const allBillingPartiesValid = bookingData.billingPartiesData
            .reduce((out, partyData) => {
                return out ? partyData.isValid : out;
            }, true);

        if (allBillingPartiesValid) {
            setCtaSettings({
                handler: addBillingParty,
                label: '+ Add Billing Party',
                disabled: allGuestData
                    .map(guest => Boolean(guest.billingPartyId))
                    .every(hasBillingParty => hasBillingParty === true),
            });
        } else {
            setCtaSettings(null);
        }

        return () => {
            setCtaSettings(null);
        };
    }, [allGuestData, bookingData]);

    return (
        <div className={classes.root}>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <Typography variant="h1" className={clsx([classes.heading, classes.padBottom])}>
                        Billing Details
                    </Typography>
                </Grid>
            </Grid>
            {bookingData.billingPartiesData.map((billingParty, index) => (
                <BillingParty
                    key={index}
                    partyData={billingParty}
                    bookingData={bookingData}
                    addBillingParty={addBillingParty}
                    removeBillingParty={removeBillingParty}
                    setBillingPartyData={setBillingPartyData}
                    setGuestBillingParty={setGuestBillingParty}
                    allGuestData={allGuestData}
                    toggleRemainingGuestsParty={toggleRemainingGuestsParty}
                    billingPartyEmpty={billingPartyEmpty}
                />
            ))}
            {errors.map((error, i) => (
                <Grid key={i} item xs={12}>
                    <Typography variant="body1" className={clsx([classes.body, classes.warningText])}>
                        {error}
                    </Typography>
                </Grid>
            ))}
        </div>
    );
};

export default Step4BillingDetails;
